import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../Auth/AuthContext';
import PlatformHome from './PlatformHome';
import OrderManagement from '../Orders/OrderManagement';
import BulkImportExport from '../Orders/BulkImportExport';
import UserManagement from '../Users/UserManagement';
import Notifications from '../Notifications/Notifications';
import InvoiceManagement from '../Invoices/InvoiceManagement';
import APIKeyManagement from '../Users/APIKeyManagement';
import { apiCall } from '../../utils/apiUtils';
import companyLogo from '../../Assets/Images/matrica-logo.webp';
import { GlobalSearchProvider, useGlobalSearch } from '../Common/GlobalSearchContext';
import GlobalSearchPill from '../Common/GlobalSearchPill';
import ProfileModal from '../Common/ProfileModal';
import APIServices from '../API/APIServices';
import APIServiceDetails from '../API/APIServiceDetails';
import APILogsTable from '../API/APILogsTable';
import AdminAPIServices from '../Admin/AdminAPIServices';
import NotificationSettings from '../Admin/NotificationSettings';
import StorageCleanup from '../Admin/StorageCleanup';
import DoqSolvPanel from '../DoqSolv/DoqSolvPanel';
import WorkflowBuilder from '../WorkflowBuilder/WorkflowBuilder';
import ESignManagement from '../ESign/ESignManagement';
import WebhookSettings from '../Settings/WebhookSettings';
import ClientWebhooksAdmin from '../Admin/ClientWebhooksAdmin';

const TAB_ROUTE_MAP = {
  dashboard: '/',
  'orders-bulk': '/bulk-orders',
  'orders-live': '/live-stamping',
  users: '/users',
  'bulk-import-export': '/import-export',
  notifications: '/notifications',
  invoices: '/invoices',
  'api-services': '/api-services',
  'notification-settings': '/email-settings',
  'storage-cleanup': '/storage-cleanup',
  doqsolv: '/doqsolv',
  settings: '/settings',
  'client-webhook': '/client-webhook',
  esign: '/esign',
  'esign-aadhaar': '/esign/aadhaar',
  'esign-digital': '/esign/digital',
  'workflow-builder': '/workflow-builder'
};

const ROUTE_TAB_MAP = Object.fromEntries(
  Object.entries(TAB_ROUTE_MAP).map(([tab, route]) => [route, tab])
);

const DashboardContent = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({});
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [dateRange, setDateRange] = useState('6months');
  const { user, logout } = useAuth();
  const { setGlobalSearchHandler, setResultHandler } = useGlobalSearch();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const [pendingSelection, setPendingSelection] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [esignExpanded, setEsignExpanded] = useState(false); // Track eSign submenu expansion
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [sidebarPrefHydrated, setSidebarPrefHydrated] = useState(false);
  const [tabSyncReady, setTabSyncReady] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const activeTabStorageKey = `dashboardActiveTab:${user?.email || user?.id || 'default'}`;
  const sidebarCollapsedStorageKey = `dashboardSidebarCollapsed:${user?.email || user?.id || 'default'}`;

  useEffect(() => {
    let isMounted = true;

    const fetchStats = async () => {
      try {
        const response = await apiCall(`/api/dashboard/stats?range=${dateRange}`);
        if (isMounted) {
          setStats(response);
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };

    fetchStats();

    return () => {
      isMounted = false;
    };
  }, [dateRange]);

  // Restore sidebar collapsed/expanded preference per user
  useEffect(() => {
    if (!user) return;
    const savedSidebarState = localStorage.getItem(sidebarCollapsedStorageKey);
    if (savedSidebarState !== null) {
      setSidebarCollapsed(savedSidebarState === 'true');
    }
    setSidebarPrefHydrated(true);
  }, [user, sidebarCollapsedStorageKey]);

  // Persist sidebar collapsed/expanded preference
  useEffect(() => {
    if (!user || !sidebarPrefHydrated) return;
    localStorage.setItem(sidebarCollapsedStorageKey, String(sidebarCollapsed));
  }, [sidebarCollapsed, user, sidebarCollapsedStorageKey, sidebarPrefHydrated]);

  useEffect(() => {
    setGlobalSearchHandler((query) => {
      if (query && query.trim()) {
        if (query.startsWith('#')) {
          setActiveTab('orders-bulk');
        } else if (query.toLowerCase().includes('invoice') || query.toUpperCase().includes('INV')) {
          setActiveTab('invoices');
        } else if (query.includes('@') || query.toLowerCase().includes('user')) {
          setActiveTab('users');
        } else {
          setActiveTab('orders-bulk');
        }
      }
    });
  }, [setGlobalSearchHandler]);

  useEffect(() => {
    setResultHandler((selection) => {
      if (!selection) return;
      const { type } = selection;
      if (type === 'order') {
        setActiveTab('orders-bulk');
        setPendingSelection(selection);
      } else if (type === 'invoice') {
        setActiveTab('invoices');
        setPendingSelection(selection);
      } else if (type === 'user') {
        setActiveTab('users');
        setPendingSelection(selection);
      }
    });
    return () => setResultHandler(null);
  }, [setResultHandler]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    const handleEsc = (e) => {
      if (e.key === 'Escape') setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, []);



  const fetchUnreadCount = async () => {
    try {
      const response = await apiCall('/api/notifications/unread-count');
      setUnreadNotificationCount(response.unread_count || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  // Fetch unread count on mount and when returning from notifications tab
  useEffect(() => {
    fetchUnreadCount();
  }, [activeTab]);

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const getTabs = () => {
    const showWorkflows = process.env.REACT_APP_ENABLE_WORKFLOWS === 'true';
    const commonTabs = [
      {
        id: 'dashboard',
        label: 'Dashboard',
        color: 'text-blue-300',
        activeColor: 'text-blue-100',
        icon: (
          <svg className="w-6 h-6 flex-shrink-0" fill="none" viewBox="0 0 24 24">
            <rect x="3" y="12" width="3" height="9" rx="1" fill="#88b2f5ff" opacity="0.8" />
            <rect x="7" y="8" width="3" height="13" rx="1" fill="#10B981" opacity="0.8" />
            <rect x="11" y="4" width="3" height="17" rx="1" fill="#F59E0B" opacity="0.8" />
            <rect x="15" y="10" width="3" height="11" rx="1" fill="#EF4444" opacity="0.8" />
            <rect x="19" y="6" width="3" height="15" rx="1" fill="#8B5CF6" opacity="0.8" />
            <line x1="2" y1="22" x2="22" y2="22" stroke="currentColor" strokeWidth="1" opacity="0.5" />
          </svg>
        )
      },
      {
        id: 'orders-bulk',
        label: 'Bulk Orders',
        color: 'text-green-300',
        activeColor: 'text-green-100',
        icon: (
          <svg className="w-6 h-6 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
          </svg>
        )
      },
      {
        id: 'orders-live',
        label: 'Live Stamping',
        color: 'text-green-300',
        activeColor: 'text-green-100',
        icon: (
          <svg className="w-6 h-6 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
            <path d="M11 21h-1l1-7H7.5a.5.5 0 01-.4-.8L13 3h1l-1 7h3.5a.5.5 0 01.4.8L11 21z" />
          </svg>
        )
      },
      {
        id: 'esign',
        label: 'eSign',
        color: 'text-emerald-300',
        activeColor: 'text-emerald-100',
        icon: (
          <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        ),
        subItems: [
          { id: 'esign-aadhaar', label: 'Aadhaar eSign', highlight: true },
          { id: 'esign-digital', label: 'Digital Signature' }
        ]
      },
      {
        id: 'notifications',
        label: 'Notifications',
        color: 'text-yellow-300',
        activeColor: 'text-yellow-100',
        badge: unreadNotificationCount,
        icon: (
          <svg className="w-6 h-6 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
          </svg>
        )
      },
    ];

    if (user.role === 'admin' || user.role === 'accountant') {
      commonTabs.push({
        id: 'invoices',
        label: 'Invoices',
        color: 'text-pink-300',
        activeColor: 'text-pink-100',
        icon: (
          <svg className="w-6 h-6 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
            <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
          </svg>
        )
      });
    }

    if (user.role === 'admin') {
      const adminTabs = [
        ...commonTabs,
        {
          id: 'users',
          label: 'Users',
          color: 'text-purple-300',
          activeColor: 'text-purple-100',
          icon: (
            <svg className="w-6 h-6 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
              <path d="M16 7c0-2.21-1.79-4-4-4S8 4.79 8 7s1.79 4 4 4 4-1.79 4-4zm-4 6c-2.67 0-8 1.34-8 4v3h16v-3c0-2.66-5.33-4-8-4z" />
            </svg>
          )
        },
        {
          id: 'bulk-import-export',
          label: 'Import/Export',
          color: 'text-orange-300',
          activeColor: 'text-orange-100',
          icon: (
            <svg className="w-6 h-6 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
              <path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z" />
            </svg>
          )
        },
        {
          id: 'api-services',
          label: 'API Services',
          color: 'text-indigo-300',
          activeColor: 'text-indigo-100',
          icon: (
            <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          )
        },
        {
          id: 'client-webhook',
          label: 'Client-Webhooks',
          color: 'text-sky-300',
          activeColor: 'text-sky-100',
          icon: (
            <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.14 0M1.394 9.393c5.857-5.858 15.355-5.858 21.213 0" />
            </svg>
          )
        },
        {
          id: 'notification-settings',
          label: 'Email Settings',
          color: 'text-teal-300',
          activeColor: 'text-teal-100',
          icon: (
            <svg className="w-6 h-6 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
            </svg>
          )
        },
        {
          id: 'storage-cleanup',
          label: 'Storage',
          color: 'text-red-300',
          activeColor: 'text-red-100',
          icon: (
            <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
            </svg>
          )
        },
        {
          id: 'doqsolv',
          label: 'DoqSolv',
          color: 'text-violet-300',
          activeColor: 'text-violet-100',
          icon: (
            <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          )
        },
      ];
      if (showWorkflows) {
        adminTabs.push({
          id: 'workflow-builder',
          label: 'Workflows',
          color: 'text-indigo-300',
          activeColor: 'text-indigo-100',
          icon: (
            <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
            </svg>
          )
        });
      }
      return adminTabs;
    } else if (user.role === 'client') {
      const clientTabs = [
        ...commonTabs,
        {
          id: 'bulk-import-export',
          label: 'Import/Export',
          color: 'text-orange-300',
          activeColor: 'text-orange-100',
          icon: (
            <svg className="w-6 h-6 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
              <path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z" />
            </svg>
          )
        },
        {
          id: 'api-services',
          label: 'API Services',
          color: 'text-purple-300',
          activeColor: 'text-purple-100',
          icon: (
            <svg className="w-6 h-6 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20.5 11H19V7c0-1.1-.9-2-2-2h-4V3.5C13 2.12 11.88 1 10.5 1S8 2.12 8 3.5V5H4c-1.1 0-1.99.9-1.99 2v3.8H3.5c1.49 0 2.7 1.21 2.7 2.7s-1.21 2.7-2.7 2.7H2V20c0 1.1.9 2 2 2h3.8v-1.5c0-1.49 1.21-2.7 2.7-2.7 1.49 0 2.7 1.21 2.7 2.7V22H17c1.1 0 2-.9 2-2v-4h1.5c1.38 0 2.5-1.12 2.5-2.5S21.88 11 20.5 11z" />
            </svg>
          )
        },
        {
          id: 'settings',
          label: 'Settings',
          color: 'text-cyan-300',
          activeColor: 'text-cyan-100',
          icon: (
            <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.573-1.066z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          )
        }
      ];

        // Always show DoqSolv (service guard handled in panel)
        clientTabs.push({
            id: 'doqsolv',
            label: 'DoqSolv',
            color: 'text-violet-300',
            activeColor: 'text-violet-100',
            icon: (
                <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
            )
        });

        if (showWorkflows) {
          clientTabs.push({
              id: 'workflow-builder',
              label: 'Workflows',
              color: 'text-indigo-300',
              activeColor: 'text-indigo-100',
              icon: (
                  <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                  </svg>
              )
          });
        }

        return clientTabs;
    } else if (user.role === 'vendor') {
      return [
        ...commonTabs,
        {
          id: 'bulk-import-export',
          label: 'Export',
          color: 'text-orange-300',
          activeColor: 'text-orange-100',
          icon: (
            <svg className="w-6 h-6 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
              <path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z" />
            </svg>
          )
        },
      ];
    }

    return commonTabs;
  };

  // Initialize tab from URL first, then fallback to persisted tab
  useEffect(() => {
    if (!user) return;

    const tabs = getTabs();
    const validTabIds = tabs.flatMap(tab => [
      tab.id,
      ...(tab.subItems ? tab.subItems.map(sub => sub.id) : [])
    ]);

    const routeTab = ROUTE_TAB_MAP[location.pathname];
    const savedTab = localStorage.getItem(activeTabStorageKey);
    const nextTab =
      (routeTab && validTabIds.includes(routeTab) && routeTab) ||
      (savedTab && validTabIds.includes(savedTab) && savedTab) ||
      'dashboard';

    if (nextTab !== activeTab) {
      setActiveTab(nextTab);
    }
    setTabSyncReady(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, user?.email, user?.id, activeTabStorageKey, location.pathname]);

  // Persist current page so refresh returns user there
  useEffect(() => {
    if (!tabSyncReady || !user || !activeTab) return;
    localStorage.setItem(activeTabStorageKey, activeTab);
  }, [activeTab, activeTabStorageKey, user, tabSyncReady]);

  // Sync URL -> tab on browser navigation (back/forward/direct route)
  useEffect(() => {
    if (!tabSyncReady) return;
    const routeTab = ROUTE_TAB_MAP[location.pathname];
    if (!routeTab) return;

    const tabs = getTabs();
    const validTabIds = tabs.flatMap(tab => [
      tab.id,
      ...(tab.subItems ? tab.subItems.map(sub => sub.id) : [])
    ]);

    if (validTabIds.includes(routeTab) && routeTab !== activeTab) {
      setActiveTab(routeTab);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, user?.role]);

  // Sync tab -> URL when menu changes
  useEffect(() => {
    if (!tabSyncReady) return;
    const targetPath = TAB_ROUTE_MAP[activeTab];
    if (!targetPath) return;
    if (location.pathname !== targetPath) {
      navigate(targetPath, { replace: true });
    }
  }, [activeTab, navigate, location.pathname, tabSyncReady]);

  const dashboardView = (
    <div
      className="space-y-2 fade-in"
      style={activeTab === 'dashboard'
        ? { width: '100%' }
        : { position: 'absolute', top: -99999, left: -99999, visibility: 'hidden', width: '100%' }
      }
    >
      <PlatformHome
        stats={stats}
        dateRange={dateRange}
        setDateRange={setDateRange}
        setActiveTab={setActiveTab}
        unreadNotificationCount={unreadNotificationCount}
      />
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'orders':
      case 'orders-bulk':
        return (
          <OrderManagement
            pendingSelection={pendingSelection?.type === 'order' ? pendingSelection : null}
            onSelectionHandled={() => setPendingSelection(null)}
            initialTab="bulk"
            showTabSwitcher={false}
          />
        );
      case 'orders-live':
        return (
          <OrderManagement
            pendingSelection={pendingSelection?.type === 'order' ? pendingSelection : null}
            onSelectionHandled={() => setPendingSelection(null)}
            initialTab="live"
            showTabSwitcher={false}
          />
        );
      case 'users':
        return <UserManagement />;
      case 'bulk-import-export':
        return <BulkImportExport />;
      case 'notifications':
        return <Notifications />;
      case 'invoices':
        return <InvoiceManagement />;
      case 'api-services':
        return user.role === 'admin' ? <AdminAPIServices /> : <APIServices />;
      case 'notification-settings':
        return <NotificationSettings />;
      case 'storage-cleanup':
        return <StorageCleanup apiBase="/api" />;
      case 'doqsolv':
        return <DoqSolvPanel />;
      case 'settings':
        return <WebhookSettings />;
      case 'client-webhook':
        return user.role === 'admin' ? (
          <ClientWebhooksAdmin />
        ) : (
          <div className="p-8 text-gray-600 rounded-xl bg-white border border-gray-200 max-w-xl">
            Client-Webhooks requires an administrator account.
          </div>
        );
      case 'esign':
      case 'esign-aadhaar':
        return <ESignManagement initialTab="aadhaar" />;
      case 'esign-digital':
        return <ESignManagement initialTab="digital" />;
      case 'workflow-builder':
        return <WorkflowBuilder />;
      default:
        return null;
    }
  };

  return (
    <div className="h-screen flex overflow-y-hidden overflow-x-hidden" style={{ backgroundColor: 'var(--color-primary-50)' }}>
      <div
        className={`
          fixed left-0 top-0 h-full z-40 transition-all duration-300 ease-in-out
          ${sidebarCollapsed ? 'w-12' : 'w-52'}
          flex flex-col bg-gradient-to-b from-blue-600 to-blue-800 shadow-lg
        `}
      >
        <div className="p-2 border-b border-blue-500 border-opacity-30 h-10 flex items-center justify-center">
          <button
            onClick={toggleSidebar}
            className="w-full flex items-center justify-center p-2 rounded-lgg bg-white bg-opacity-10 hover:bg-opacity-20 transition-all duration-200"
            title={sidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
          >
            <svg
              className={`w-6 h-6 text-white transition-transform duration-300 ${sidebarCollapsed ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
            {!sidebarCollapsed && (
              <span className="ml-2 text-white text-sm font-medium">Menu</span>
            )}
          </button>
        </div>
        <nav className="flex-1 px-1 py-2">
          <div className="space-y-1">
            {getTabs().map((tab) => (
              <div key={tab.id}>
                <button
                  onClick={() => {
                    if (tab.subItems && !sidebarCollapsed) {
                      if (tab.id === 'esign') {
                        setEsignExpanded(!esignExpanded);
                      }
                    } else {
                      setActiveTab(tab.id);
                    }
                  }}
                  className={`
                    w-full flex items-center px-2 py-2 rounded-lgg text-left
                    transition-all duration-200 group relative
                    ${activeTab === tab.id || (tab.id === 'esign' && (activeTab === 'esign-aadhaar' || activeTab === 'esign-digital'))
                      ? 'bg-white bg-opacity-20 text-white shadow-md'
                      : 'text-blue-100 hover:bg-white hover:bg-opacity-10 hover:text-white'
                    }
                    ${sidebarCollapsed ? 'justify-center' : 'justify-between'}
                  `}
                  title={sidebarCollapsed ? tab.label : ''}
                >
                  <div className="flex items-center">
                    <span
                      className={`flex-shrink-0 transition-colors duration-200 relative ${activeTab === tab.id || (tab.id === 'esign' && (activeTab === 'esign-aadhaar' || activeTab === 'esign-digital')) ? tab.activeColor : tab.color
                        }`}
                      style={tab.badge > 0 ? { paddingRight: '8px' } : {}}
                    >
                      {tab.icon}
                      {/* Badge for unread notifications */}
                      {tab.badge > 0 && (
                        <span
                          className={`absolute bg-red-500 text-white text-xs font-bold flex items-center justify-center min-w-[16px] h-[16px] ${tab.badge >= 10 ? 'px-1 rounded-full' : 'rounded-full'
                            }`}
                          style={{ fontSize: '9px', top: '-6px', right: '-4px' }}
                        >
                          {tab.badge > 99 ? '99+' : tab.badge}
                        </span>
                      )}
                    </span>
                    {!sidebarCollapsed && (
                      <span className="ml-3 text-sm font-medium truncate">
                        {tab.label}
                      </span>
                    )}
                  </div>
                  {!sidebarCollapsed && tab.subItems && (
                    <svg className={`w-4 h-4 transition-transform duration-200 ${(tab.id === 'orders' && ordersExpanded) || (tab.id === 'esign' && esignExpanded) ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                  {sidebarCollapsed && (
                    <div className="absolute left-full ml-3 px-3 py-2 bg-gray-900 text-white text-sm rounded-lgg opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-50 shadow-lg">
                      {tab.label}
                    </div>
                  )}
                </button>
                {/* Sub-items for expandable menus */}
                {!sidebarCollapsed && tab.subItems && (
                  (tab.id === 'esign' && esignExpanded)
                ) && (
                  <div className="ml-8 mt-1 space-y-1">
                    {tab.subItems.map((sub) => (
                      <button
                        key={sub.id}
                        onClick={() => setActiveTab(sub.id)}
                        className={`
                          w-full text-left px-3 py-2 rounded-lgg text-sm transition-all duration-200 flex items-center
                          ${activeTab === sub.id
                            ? 'bg-white bg-opacity-15 text-white font-bold'
                            : 'text-blue-200 hover:bg-white hover:bg-opacity-10 hover:text-white'
                          }
                        `}
                      >
                        {sub.label}
                        {sub.highlight && (
                          <span className="ml-2 relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </nav>
      </div>
      <div className={`flex-1 flex flex-col transition-all duration-300 ${sidebarCollapsed ? 'ml-12' : 'ml-52'}`}>
        <nav className="border-b shadow-sm bg-white" style={{ borderColor: 'var(--color-primary-200)' }}>
          <div className="px-3">
            <div className="flex items-center h-10">
              <div className="flex items-center flex-none">
                <div className="flex items-center">
                  <img
                    src={companyLogo}
                    alt="Company Logo"
                    className="w-28 h-auto mr-2"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center mr-3 shadow-lg" style={{ background: 'linear-gradient(135deg, var(--color-primary-500), var(--color-primary-600)', display: 'none' }}>
                    <span className="text-white text-lg font-bold">M</span>
                  </div>
                </div>
              </div>
              <div className="flex-1 flex items-center justify-center">
                {/* <GlobalSearchPill /> */}
              </div>
              <div className="flex items-center justify-end flex-none" ref={userMenuRef}>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setUserMenuOpen((v) => !v)}
                    aria-haspopup="true"
                    aria-expanded={userMenuOpen}
                    className="flex items-center px-2 py-1 rounded-full transition hover:brightness-90"
                  >
                    <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, var(--color-primary-500), var(--color-primary-600))' }}>
                      <span className="text-white text-xs font-medium">
                        {(user?.name || user?.fullName || user?.username || '?').charAt(0).toUpperCase()}
                      </span>
                    </div>
                  </button>

                  {userMenuOpen && (
                    <div
                      className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-xl ring-1 ring-black ring-opacity-5 z-50 overflow-hidden"
                      role="menu"
                      aria-label="User menu"
                    >
                      <div className="p-4 border-b" style={{ borderColor: 'var(--color-primary-100)' }}>
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center mr-3 shadow-md" style={{ background: 'linear-gradient(135deg, var(--color-primary-500), var(--color-primary-600))' }}>
                            <span className="text-white text-base font-medium">
                              {(user?.name || user?.fullName || user?.username || '?').charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold text-gray-900 truncate">{user?.name || user?.fullName || user?.username || 'User'}</div>
                            {user?.email && (
                              <div className="text-xs text-gray-500 truncate">{user.email}</div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="p-2">
                        <button
                          onClick={() => { setUserMenuOpen(false); setShowProfileModal(true); }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lgg flex items-center"
                          role="menuitem"
                        >
                          <svg className="w-4 h-4 mr-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          My Profile
                        </button>
                      </div>

                      <div className="p-3 space-y-1 text-sm">
                        {user?.mobile && (
                          <div className="flex items-center text-gray-700 px-2 py-2 rounded-lgg">
                            <svg className="w-4 h-4 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M2 3a1 1 0 011-1h3.28a1 1 0 01.948.684l1.498 4.494a1 1 0 01-.502 1.2l-1.257.628a11.042 11.042 0 005.516 5.516l.628-1.257a1 1 0 011.2-.502l4.494 1.498A1 1 0 0118 16.72V20a1 1 0 01-1 1h-1C7.82 21 1 14.18 1 6V5a1 1 0 011-1z" />
                            </svg>
                            <span className="truncate">{user.mobile}</span>
                          </div>
                        )}
                        {!user?.mobile && (user?.phone || user?.contactNumber) && (
                          <div className="flex items-center text-gray-700 px-2 py-2 rounded-lgg">
                            <svg className="w-4 h-4 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M2 3a1 1 0 011-1h3.28a1 1 0 01.948.684l1.498 4.494a1 1 0 01-.502 1.2l-1.257.628a11.042 11.042 0 005.516 5.516l.628-1.257a1 1 0 011.2-.502l4.494 1.498A1 1 0 0118 16.72V20a1 1 0 01-1 1h-1C7.82 21 1 14.18 1 6V5a1 1 0 011-1z" />
                            </svg>
                            <span className="truncate">{user.phone || user.contactNumber}</span>
                          </div>
                        )}
                        {user?.email && (
                          <div className="flex items-center text-gray-700 px-2 py-2 rounded-lgg">
                            <svg className="w-4 h-4 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M2.94 6.34A2 2 0 014.4 5h11.2a2 2 0 011.46.34L10 10.6 2.94 6.34zM18 8.12l-7.4 4.63a1 1 0 01-1.2 0L2 8.12V14.6A2 2 0 004 16.6h12a2 2 0 002-2V8.12z" />
                            </svg>
                            <span className="truncate">{user.email}</span>
                          </div>
                        )}
                      </div>

                      <div className="p-3 border-t" style={{ borderColor: 'var(--color-primary-100)' }}>
                        <button
                          onClick={() => { setUserMenuOpen(false); logout(); }}
                          className="w-full btn-danger px-4 py-2 text-sm"
                          role="menuitem"
                        >
                          Logout
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </nav>
        <div className="flex-1 p-1.5 overflow-auto min-h-0">
          <div className="w-full min-h-full min-w-[700px] flex flex-col relative">
            {dashboardView}
            {renderContent()}
          </div>
        </div>
      </div>
      {!sidebarCollapsed && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarCollapsed(true)}
        />
      )}

      {showProfileModal && (
        <ProfileModal onClose={() => setShowProfileModal(false)} />
      )}
    </div>
  );
};

const Dashboard = () => {
  return (
    <GlobalSearchProvider>
      <DashboardContent />
      {/* Profile Modal is rendered inside DashboardContent but we need to pass props or context if needed. 
          Actually, DashboardContent has the state, so we render it there. 
          Wait, DashboardContent return statement ends at line 522. 
          I need to add it before the closing div of DashboardContent. 
      */}
    </GlobalSearchProvider>
  );
};

export default Dashboard;